import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  History,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Calendar,
  FileText,
  Loader2,
  Trash2,
} from "lucide-react";
import { toast } from "react-hot-toast";
import documentSearchService, {
  KBSessionListItem,
  KBSearchRun,
} from "../../services/document-search.service";
import MarkdownRenderer from "./MarkdownRenderer";

interface KnowledgeBaseHistoryTabProps {
  documentStoreId: string;
}

export interface KnowledgeBaseHistoryTabRef {
  refreshHistory: () => void;
}

const KnowledgeBaseHistoryTab = forwardRef<
  KnowledgeBaseHistoryTabRef,
  KnowledgeBaseHistoryTabProps
>(({ documentStoreId }, ref) => {
  const [sessionList, setSessionList] = useState<KBSessionListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [detailsCache, setDetailsCache] = useState<
    Record<string, KBSearchRun[]>
  >({});
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set());
  const [skip, setSkip] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 10;

  // Track expanded file responses within runs
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchSessionList();
  }, [documentStoreId, skip]);

  const fetchSessionList = async () => {
    setIsLoading(true);
    try {
      const response = await documentSearchService.listKBSessions(
        documentStoreId,
        skip,
        limit,
      );
      if (response.success) {
        setSessionList(response.data.sessions || []);
        setTotalCount(response.data.total_count || 0);
      } else {
        toast.error("Failed to fetch session history");
      }
    } catch (error: any) {
      console.error("Error fetching KB session list:", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch session history",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refreshHistory: () => {
      if (skip === 0) {
        fetchSessionList();
      } else {
        setSkip(0); // useEffect will call fetchSessionList
      }
    },
  }));

  const toggleAccordion = async (sessionId: string) => {
    // Check current state to decide expand vs collapse
    const isCurrentlyExpanded = expandedItems.has(sessionId);

    // Update expanded state immediately so UI responds instantly (Bug 1 fix)
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (isCurrentlyExpanded) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });

    // If expanding and details not cached, fetch them
    if (!isCurrentlyExpanded && !detailsCache[sessionId]) {
      setLoadingDetails(prev => new Set(prev).add(sessionId));
      try {
        const response =
          await documentSearchService.getKBSession(sessionId);
        if (response.success) {
          const runs = response.data.search_runs || [];
          setDetailsCache(prev => ({
            ...prev,
            [sessionId]: runs,
          }));
          // Auto-expand file responses in the latest run
          const latestRun = runs[runs.length - 1];
          if (latestRun) {
            const fileResponses = latestRun.file_responses || [];
            setExpandedFiles(prev => {
              const newSet = new Set(prev);
              fileResponses.forEach((f) => {
                newSet.add(`${latestRun._id}-${f.file_id}`);
              });
              return newSet;
            });
          }
        } else {
          toast.error("Failed to fetch session details");
          // Collapse back on failure
          setExpandedItems(prev => {
            const newSet = new Set(prev);
            newSet.delete(sessionId);
            return newSet;
          });
        }
      } catch (error: any) {
        console.error("Error fetching KB session detail:", error);
        toast.error(
          error.response?.data?.message ||
            "Failed to fetch session details",
        );
        // Collapse back on error
        setExpandedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(sessionId);
          return newSet;
        });
      } finally {
        setLoadingDetails((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sessionId);
          return newSet;
        });
      }
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      const response =
        await documentSearchService.deleteKBSession(sessionId);
      if (response.success) {
        toast.success("Session deleted");
        fetchSessionList();
        setDetailsCache((prev) => {
          const newCache = { ...prev };
          delete newCache[sessionId];
          return newCache;
        });
        setExpandedItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sessionId);
          return newSet;
        });
      }
    } catch (error: any) {
      console.error("Error deleting KB session:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete session",
      );
    }
  };

  const handleDeleteRun = async (sessionId: string, runId: string) => {
    try {
      const response = await documentSearchService.deleteKBSearchRun(
        sessionId,
        runId,
      );
      if (response.success) {
        toast.success("Search run deleted");
        setDetailsCache(prev => ({
          ...prev,
          [sessionId]: response.data.search_runs || [],
        }));
      }
    } catch (error: any) {
      console.error("Error deleting search run:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete search run",
      );
    }
  };

  const toggleFileExpanded = (key: string) => {
    setExpandedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return "\u{1F4C4}";
      case "doc":
      case "docx":
        return "\u{1F4DD}";
      case "xls":
      case "xlsx":
        return "\u{1F4CA}";
      case "txt":
        return "\u{1F4CB}";
      case "pptx":
      case "ppt":
        return "\u{1F4D1}";
      case "csv":
        return "\u{1F4C8}";
      default:
        return "\u{1F4C1}";
    }
  };

  const handleLoadMore = () => {
    setSkip(skip + limit);
  };

  const handleLoadPrevious = () => {
    setSkip(Math.max(0, skip - limit));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 rounded-full border-2 border-t-violet-600 border-r-transparent border-b-violet-400 border-l-transparent animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-2 border-t-purple-400 border-r-transparent border-b-purple-600 border-l-transparent animate-[spin_3s_linear_infinite_reverse]"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <History className="w-5 h-5 text-violet-600 animate-pulse" />
            </div>
          </div>
          <p className="text-sm text-slate-600">Loading session history...</p>
        </div>
      </div>
    );
  }

  if (sessionList.length === 0) {
    return (
      <div className="bg-gradient-to-br from-slate-50 to-violet-50/20 rounded-lg p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-violet-100 to-purple-100 mb-3">
          <History className="h-8 w-8 text-violet-600" />
        </div>
        <h3 className="text-sm font-bold text-slate-900 mb-2">
          No Session History
        </h3>
        <p className="text-xs text-slate-600 max-w-md mx-auto">
          Your knowledge base search history will appear here once you start
          making queries.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <History className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-sm font-bold text-slate-800">Session History</h2>
          <span className="text-xs text-slate-500">
            ({totalCount} {totalCount === 1 ? "session" : "sessions"})
          </span>
        </div>
      </div>

      <div className="space-y-2">
        {sessionList.map((item) => {
          const isExpanded = expandedItems.has(item._id);
          const isLoadingDetail = loadingDetails.has(item._id);
          const runs = detailsCache[item._id];

          return (
            <div
              key={item._id}
              className="border border-slate-200 rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow"
            >
              {/* Accordion Header */}
              <div
                onClick={() => toggleAccordion(item._id)}
                className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-slate-600" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-3.5 h-3.5 text-violet-600 flex-shrink-0" />
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {item.session_title || item.search_query}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(item.last_search_at || item.createdAt)}
                      </span>
                      <span className="text-slate-300">&bull;</span>
                      <span>
                        {item.run_count}{" "}
                        {item.run_count === 1 ? "run" : "runs"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSession(item._id);
                    }}
                    className="p-1.5 rounded-lg border border-slate-200 hover:bg-red-50 hover:border-red-300 transition-all"
                    title="Delete session"
                  >
                    <Trash2 className="w-3 h-3 text-slate-400 hover:text-red-500" />
                  </button>
                </div>
              </div>

              {/* Accordion Content */}
              {isExpanded && (
                <div className="border-t border-slate-200 bg-slate-50">
                  {isLoadingDetail ? (
                    <div className="p-4 flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 text-violet-600 animate-spin" />
                      <span className="text-sm text-slate-600">
                        Loading session details...
                      </span>
                    </div>
                  ) : runs && runs.length > 0 ? (
                    <div className="p-3 space-y-3">
                      {runs.map((run, runIndex) => (
                        <div
                          key={run._id}
                          className="bg-white rounded-lg border border-slate-200 overflow-hidden"
                        >
                          {/* Run Header */}
                          <div className="flex items-center justify-between px-3 py-2 bg-slate-50/70 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 rounded-md bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                <span className="text-[10px] font-bold text-white">
                                  {runIndex + 1}
                                </span>
                              </div>
                              <span className="text-xs font-semibold text-slate-700">
                                Run #{runIndex + 1}
                              </span>
                              <span className="text-[10px] text-slate-400">
                                &bull; {(run.file_responses || []).length}{" "}
                                {(run.file_responses || []).length === 1
                                  ? "file"
                                  : "files"}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-slate-400">
                                {formatDate(run.createdAt)}
                              </span>
                              {runs.length > 1 && (
                                <button
                                  onClick={() =>
                                    handleDeleteRun(item._id, run._id)
                                  }
                                  className="p-1 rounded hover:bg-red-50 transition-colors"
                                  title="Delete this run"
                                >
                                  <Trash2 className="w-3 h-3 text-slate-300 hover:text-red-500" />
                                </button>
                              )}
                            </div>
                          </div>

                          {/* File Responses */}
                          <div className="divide-y divide-slate-100">
                            {(run.file_responses || []).map((file) => {
                              const fileKey = `${run._id}-${file.file_id}`;
                              const isFileExpanded =
                                expandedFiles.has(fileKey);

                              return (
                                <div key={fileKey}>
                                  <div
                                    className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors"
                                    onClick={() =>
                                      toggleFileExpanded(fileKey)
                                    }
                                  >
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <span className="text-sm flex-shrink-0">
                                        {getFileIcon(file.file_name)}
                                      </span>
                                      <span className="text-xs font-medium text-slate-700 truncate">
                                        {file.file_name}
                                      </span>
                                    </div>
                                    <div className="flex-shrink-0 ml-2">
                                      {isFileExpanded ? (
                                        <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                                      ) : (
                                        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                                      )}
                                    </div>
                                  </div>
                                  {isFileExpanded && (
                                    <div className="px-3 py-3 bg-slate-50/50 border-t border-slate-100">
                                      <MarkdownRenderer
                                        content={file.response}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-sm text-slate-500">
                      No search runs available
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalCount > limit && (
        <div className="flex items-center justify-between pt-3 border-t border-slate-200">
          <button
            onClick={handleLoadPrevious}
            disabled={skip === 0}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Previous
          </button>
          <span className="text-sm text-slate-600">
            Showing {skip + 1} - {Math.min(skip + limit, totalCount)} of{" "}
            {totalCount}
          </span>
          <button
            onClick={handleLoadMore}
            disabled={skip + limit >= totalCount}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
});

KnowledgeBaseHistoryTab.displayName = "KnowledgeBaseHistoryTab";

export default KnowledgeBaseHistoryTab;
